import { Spinner, makeStyles, shorthands } from '@fluentui/react-components';
import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { appInsights } from '../../AppInsights';
import { useNotification } from '../../Contexts/NotificationContext';
import { useEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import MarkdownPresenter from '../Common/MarkdownPresenter';
import AppLogoWithTitle from '../Common/AppLogoWithTitle';
import { CDialog } from '../Dialogs/CDialog';
import { useUserContext } from '../../Contexts/UserContext';
import { UserInteractionEventProperties, EventName_UserInteraction, EventProperty_Value_ApplicationType, EventProperty_Value_DeclineToU } from '../../store/models/customEvents';
import { logout } from '../../Security/azureAdAuthentication';

export interface EnrollmentDialogProps {
    onAccept: () => Promise<void>;
    open: boolean;
    acceptText?: string;
}

const useStyles = makeStyles({
    dialogContentWrapper: {
        '& a': {
            color: '#ff5e31',
        }
    },
    titleContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.gap('0.5rem'),
        marginBottom: '1.5em',
        '& p, & h1, & h2, & h3, & h4, & h5, & h6': {
            borderBottomWidth: 0,
            paddingBottom: 0,
            marginBottom: 0,
        }
    },
    contentArea: {
        textAlign: 'justify',
        overflowY: 'auto',
        maxHeight: '50vh',
        marginBottom: '1em',
    },
    highlight: {
        display: 'inline-block',
        backgroundColor: '#ffb58a',
        ...shorthands.padding('0.5em'),
        marginTop: '1em',
        marginBottom: '1em',
        '& *:not(a)': {
            color: '#000040 !important',
        },
        '& a': {
            color: '#ff5e31 !important',
        }
    },
});

const EnrollmentDialog: FC<EnrollmentDialogProps> = ({ acceptText, open, onAccept }) => {
    const NoSecondsWaitLoadBeforeShowingErrorTerms = 2;  // Note: If the "Loading" flag is true, it will not show the error message anyway, so this constant is pretty safe even with very slow loading times.

    const classes = useStyles();
    const { t } = useTranslation();
    const [titleContent, setTitleContent] = useState<string>('');
    const [mainTermsContent, setMainTermsContent] = useState<string>('');
    const [lastParagraphContent, setLastParagraphContent] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [canShowErrorMessage, setCanShowErrorMessage] = useState(false);
    const { dispatchNotification } = useNotification();
    const { userProfile } = useUserContext();    // Add a delay before allowing the error message to show

    useEffect(() => {
        const timer = setTimeout(() => {
            setCanShowErrorMessage(true);
        }, NoSecondsWaitLoadBeforeShowingErrorTerms*1000);

        return () => clearTimeout(timer);
    }, []);

    useEnterpriseGPTApiCall(async (client, signal) => {
        setLoading(true);
        setTitleContent('');
        setMainTermsContent('');
        setLastParagraphContent('');
        try {
            const content = await client.getTermsOfUse(signal);
            const allSections = content.trim().split('\n\n');

            if (allSections.length === 0) {
                // Contenu vide
            } else if (allSections.length === 1) {
                // Seulement un titre
                setTitleContent(allSections[0]);
            } else if (allSections.length === 2) {
                // Titre et dernier paragraphe
                setTitleContent(allSections.shift() ?? '');
                setLastParagraphContent(allSections.pop() ?? '');
            } else { // 3 sections ou plus
                // Titre, contenu principal, et dernier paragraphe
                setTitleContent(allSections.shift() ?? '');
                setLastParagraphContent(allSections.pop() ?? '');
                setMainTermsContent(allSections.join('\n\n')); // Le reste au milieu
            }

        } catch (error) {
            if ((error as Error).message === 'canceled') return;
            appInsights.trackException({ exception: error as Error });
            dispatchNotification('error.api.get.termsOfUse', 'error');
            setTitleContent('');
            setMainTermsContent('');
            setLastParagraphContent('');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleAccept = () => {
        setLoading(true);
        onAccept().finally(() => {
            setLoading(false);
        });
    };

    const handleDecline = async () => {
        appInsights.trackEvent({name: EventName_UserInteraction, properties: 
            {
                applicationType: EventProperty_Value_ApplicationType,
                userId: userProfile?.username,
                interactionType: EventProperty_Value_DeclineToU
            } as UserInteractionEventProperties
        });
        logout();
    };

    const isContentEmpty = !titleContent && !mainTermsContent && !lastParagraphContent;

    return (
        <CDialog
            open={open}
            onPrimaryAction={handleAccept}
            primaryActionText={acceptText ?? t('YES')}
            disablePrimaryAction={loading || isContentEmpty}
            onSecondaryAction={handleDecline}
            secondaryActionText={t('NO')}
        >
            <div className={classes.dialogContentWrapper}>
                {loading && <Spinner appearance='primary' label={t('loading')} />}

                {/* Titre et Logo en colonne */}
                {!loading && titleContent && (
                    <div className={classes.titleContainer}>
                        {/* Mettre l'icône en premier */}
                        <AppLogoWithTitle collapsed={false} />
                        <MarkdownPresenter text={titleContent} />
                    </div>
                )}

                {!loading && mainTermsContent && (
                    <div className={classes.contentArea}>
                        <MarkdownPresenter text={mainTermsContent} />
                    </div>
                )}

                {!loading && lastParagraphContent && (
                    <div className={classes.highlight}>
                        <MarkdownPresenter text={lastParagraphContent} />
                    </div>
                )}
                {!loading && isContentEmpty && canShowErrorMessage && (
                    <div>{t('enrollment.termsNotAvailable')}</div>
                )}
            </div>
        </CDialog>
    );
};

export default EnrollmentDialog;
